import {
  FormControl,
  FormHelperText,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
} from "@material-ui/core";
import { postNewUser } from "@State/index";
import {
  selectClientId,
  selectCustomerId,
  selectUserId,
} from "@State/selectors";
import { FetchOnceAutocomplete } from "@View/common/form/control/FetchOnceAutocomplete";
import ButtonSave from "@View/components/buttons/ButtonSave";
import DefaultInput from "@View/components/formInputs/DefaultInput";
import DefaultSelect from "@View/components/formInputs/DefaultSelect";
import ModalLayout from "@View/components/ModalLayout";
import { ROLE_PAGES } from "@View/constants";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { CopyIcon } from "@View/components/svgComponents/CopyIcon";
import { searchClients } from "@Utils/commonAPI";
import { Checkbox } from "@Components/form/control/Checkbox";
import Typography from "@material-ui/core/Typography";
import { generatePassword, isValidPassword } from "../../../../security";
import { getCustomerId, getRole } from "@Security/stateHelpers";
import getAPI from "@API/index";

const NewUserPage = ({
  customerId,
  prevPage,
  open,
  handleClose,
}) => {
  const initiatorIdSelectors = {
    [ROLE_PAGES.user]: selectUserId,
    [ROLE_PAGES.users]: selectUserId,
    [ROLE_PAGES.client]: selectClientId,
    [ROLE_PAGES.customer]: selectCustomerId,
  };

  const history = useHistory();
  const currentLocation = useLocation();
  const dispatch = useDispatch();
  const initiatorSelector = initiatorIdSelectors[prevPage];
  const initiatorId = useSelector(initiatorSelector);
  const { t } = useTranslation();
  // CustomerUser can only add users to their own customer.
  const ownCustomerId = getRole() === "CustomerUser" ? getCustomerId() : null;

  const deleteObjFunc = (roleV) => {
    if (roleV === "Admin") {
      setNewData((prevData) => {
        let newData = { ...prevData };
        delete newData.CustomerId;
        delete newData.ClientId;
        delete newData.clientName;
        delete newData.customerName;
        return newData;
      });
    } else if (
      roleV === "CustomerUser" ||
      roleV === "CustomerGuest" ||
      roleV === "CustomerManager" ||
      roleV === "CustomerEmployee"
    ) {
      setNewData((prevData) => {
        let newData = { ...prevData };
        delete newData.ClientId;
        delete newData.clientName;
        return newData;
      });
    } else if (roleV === "ClientUser" || roleV === "ClientGuest") {
      setNewData((prevData) => {
        let newData = { ...prevData };
        delete newData.CustomerId;
        delete newData.customerName;
        return newData;
      });
    }
  };

  const [newData, setNewData] = useState({
    role:
      prevPage !== "customer"
        ? "ClientUser"
        : getRole() === "CustomerUser"
          ? "CustomerManager"
          : "CustomerUser",
    userName: "",
    fullName: "",
    email: "",
    password: "",
    prevPage,
    initiatorId,
  });
  const [errors, setErrors] = useState({});

  const handleChange = (value, label = null) => {
    if (Object.is(label, null)) {
      setNewData({ ...newData, ...value });
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        Object.keys(value).forEach((key) => {
          delete newErrors[key];
        });
        return newErrors;
      });
    } else {
      setNewData({ ...newData, [label]: value });
      setErrors((prevErrors) => {
        if (!prevErrors[label]) return prevErrors;
        const newErrors = { ...prevErrors };
        delete newErrors[label];
        return newErrors;
      });
    }
  };

  const handleClientChange = (value) => {
    let obj = {
      ClientId: value.id,
      clientName: value.name,
    };
    setNewData({ ...newData, ...obj });
    setErrors((prevErrors) => {
      if (!prevErrors.ClientId) return prevErrors;
      const newErrors = { ...prevErrors };
      delete newErrors.ClientId;
      return newErrors;
    });
  };

  const handleCustomerChange = (value) => {
    let obj = {
      CustomerId: value.id,
      customerName: value.name,
    };
    setNewData({ ...newData, ...obj });
    setErrors((prevErrors) => {
      if (!prevErrors.CustomerId) return prevErrors;
      const newErrors = { ...prevErrors };
      delete newErrors.CustomerId;
      return newErrors;
    });
  };

  function validateEmail(email) {
    var re = /\S+@\S+\.\S+/;
    return re.test(email);
  }

  const customerRoles = [
    "CustomerUser",
    "CustomerGuest",
    "CustomerManager",
    "CustomerEmployee",
  ];
  const clientRoles = ["ClientUser", "ClientGuest"];

  const validate = () => {
    const result = {};
    const userName = newData.userName || "";
    const email = newData.email || "";
    const password = newData.password || "";

    if (!userName.trim()) {
      result.userName = "validation.LoginRequired";
    }
    if (!email.trim()) {
      result.email = "validation.EmailRequired";
    } else if (!validateEmail(email)) {
      result.email = "validation.InvalidEmail";
    }
    if (
      customerRoles.includes(newData.role) &&
      !ownCustomerId &&
      !newData.CustomerId
    ) {
      result.CustomerId = "validation.CustomerRequired";
    }
    if (clientRoles.includes(newData.role) && !newData.ClientId) {
      result.ClientId = "validation.ClientRequired";
    }
    if (!typesWithBlankPassword.includes(newData.role) && !password.trim()) {
      result.password = "validation.PasswordRequired";
    }
    if (
      typesWithBlankPassword.includes(newData.role) &&
      isPassVisible &&
      !isValidPassword(password)
    ) {
      result.password = "validation.PasswordInvalid";
    }

    return result;
  };

  const handleClick = async (event) => {
    event.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload =
      ownCustomerId && customerRoles.includes(newData.role)
        ? { ...newData, CustomerId: ownCustomerId }
        : newData;

    try {
      await dispatch(postNewUser(payload, history));
      handleClose();
    } catch (error) {
      // The API layer already shows an error snackbar. Keep the modal open.
    }
  };

  // all roles
  let types = [
    { value: "Admin" },
    { value: "CustomerUser" },
    { value: "CustomerManager" },
    { value: "CustomerEmployee" },
    { value: "CustomerGuest" },
    { value: "ClientUser" },
    { value: "ClientGuest" },
  ];

  if (currentLocation.pathname.includes("/client/details/")) {
    types = [
      { value: "ClientUser" },
      { value: "ClientGuest" },
      { value: "CustomerManager" },
      { value: "CustomerEmployee" },
      { value: "CustomerGuest" },
    ];
  } else if (currentLocation.pathname.includes("/customer/details/")) {
    types = [
      { value: "CustomerUser" },
      { value: "CustomerManager" },
      { value: "CustomerEmployee" },
      { value: "CustomerGuest" },
    ];
  }

  if (getRole() === "CustomerUser") {
    types = types.filter((r) => r.value !== "Admin" && r.value !== "CustomerUser");
  }
  if (["CustomerManager", "CustomerEmployee"].includes(getRole())) {
    types = [{ value: "ClientUser" }, { value: "ClientGuest" }];
  }

  const typesWithBlankPassword = [
    "ClientUser",
    "ClientGuest",
    "CustomerUser",
    "CustomerGuest",
    "CustomerManager",
    "CustomerEmployee",
  ];
  const [pickRole, setRole] = useState(newData.role);
  const fetchCustomerOptions = () =>
    getAPI()
      .request({
        url: "/api/Customer/search",
      })
      .then((result) => result.map((e) => ({ id: e.guid, name: e.name })));

  let getDefClientId = async () => {
    let res = await searchClients();
    let idx = res.findIndex((el) => el.id === newData.initiatorId);
    if (idx === -1) return;
    let obj = {
      ClientId: res[idx].id,
      clientName: res[idx].name,
    };
    setNewData({ ...newData, ...obj });
    setErrors((prevErrors) => {
      if (!prevErrors.ClientId) return prevErrors;
      const newErrors = { ...prevErrors };
      delete newErrors.ClientId;
      return newErrors;
    });
  };

  let getDefCustomerId = async () => {
    let res = await fetchCustomerOptions();
    let idx = await res.findIndex((el) => el.id === newData.initiatorId);
    if (idx === -1) return;
    let obj = {
      CustomerId: res[idx].id,
      customerName: res[idx].name,
    };
    setNewData({ ...newData, ...obj });
    setErrors((prevErrors) => {
      if (!prevErrors.CustomerId) return prevErrors;
      const newErrors = { ...prevErrors };
      delete newErrors.CustomerId;
      return newErrors;
    });
  };

  React.useEffect(() => {
    if (prevPage === "customer" && !ownCustomerId) {
      getDefCustomerId();
    } else if (prevPage === "client") {
      getDefClientId();
    }
  }, []);

  const handleCopyText = () => {
    navigator.clipboard.writeText(newData.password);
  };

  const [isPassVisible, setIsPassVisible] = useState(false);

  const footer = (
    <ButtonSave
      marginTop="0"
      disabled={
        typesWithBlankPassword.includes(newData.role) && isPassVisible
          ? !isValidPassword(newData.password)
          : false
      }
    >
      buttons.Save
    </ButtonSave>
  );

  return (
    <ModalLayout
      open={open}
      onClose={handleClose}
      title="h1.New User"
      onSubmit={handleClick}
      footer={footer}
    >
      <DefaultInput
        value={newData.userName}
        label="Login"
        onChange={(value) => handleChange(value, "userName")}
        error={!!errors.userName}
        helperText={errors.userName}
      />
      <DefaultInput
        value={newData.email}
        label="inputs.Email"
        onChange={(value) => handleChange(value, "email")}
        error={!!errors.email}
        helperText={errors.email}
      />
      <DefaultInput
        value={newData.fullName}
        label="FullName"
        onChange={(value) => handleChange(value, "fullName")}
      />
      <FormControl className={"PassInput"}>
        <InputLabel htmlFor="standard-adornment-password">
          {t("Password")}
        </InputLabel>
        <Input
          id="standard-adornment-password"
          type={"text"}
          value={newData.password}
          disabled={
            typesWithBlankPassword.includes(newData.role) && !isPassVisible
          }
          error={!!errors.password}
          onChange={(event) => handleChange(event.target.value, "password")}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleCopyText}
                onMouseDown={null}
              >
                <CopyIcon />
              </IconButton>
            </InputAdornment>
          }
        />
        {errors.password ? (
          <FormHelperText error>{t(errors.password)}</FormHelperText>
        ) : null}
      </FormControl>
      {typesWithBlankPassword.includes(newData.role) ? (
        <>
          <Typography
            style={{
              color: "#A1ADBA",
              fontSize: "14px",
            }}
          >
            {isPassVisible ? t("passCriteria") : t("passOnFirstLoginSetup")}
          </Typography>
          <div style={{ paddingBottom: 15 }}>
            <Checkbox
              label={t("setPass")}
              value={isPassVisible}
              onChange={(value) => {
                setIsPassVisible(value);
                handleChange(value ? generatePassword() : "", "password");
              }}
              plain
            />
          </div>
        </>
      ) : null}
      {/* <DefaultInput
          value={newData.password}
          label="Password"
          onChange={(value) => handleChange(value, "password")} /> */}
      <DefaultSelect
        value={`${newData.role}`}
        label="inputs.Role"
        style={{ width: "100%" }}
        dropdowns={types}
        onChange={(value) => {
          handleChange({
            role: value,
            password: typesWithBlankPassword.includes(value)
              ? newData.password
              : generatePassword(),
          });
          setRole(value);
          deleteObjFunc(value);
          if (typesWithBlankPassword.includes(value)) {
            if (newData.password) {
              setIsPassVisible(true);
            }
          } else {
            setIsPassVisible(false);
          }
        }}
      />

      {customerRoles.includes(pickRole) && !ownCustomerId ? (
        <FetchOnceAutocomplete
          value={
            newData.customerName === undefined
              ? ""
              : `${newData.customerName}`
          }
          id="customer"
          label={t("Customer")}
          onChange={handleCustomerChange}
          fetchOptions={fetchCustomerOptions}
          error={!!errors.CustomerId}
          helperText={errors.CustomerId ? t(errors.CustomerId) : undefined}
        />
      ) : null}

      {pickRole === "ClientUser" || pickRole === "ClientGuest" ? (
        <FetchOnceAutocomplete
          value={
            newData.clientName === undefined ? "" : `${newData.clientName}`
          }
          id="client"
          label={t("Client")}
          onChange={handleClientChange}
          fetchOptions={searchClients}
          error={!!errors.ClientId}
          helperText={errors.ClientId ? t(errors.ClientId) : undefined}
        />
      ) : null}
    </ModalLayout>
  );
};

export default NewUserPage;
